import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../Redux/Actions';
import ModalComponents from '../../../../component/ModalComponent/ModalComponent';
import { Form } from 'react-bootstrap';
import Button from '../../../../component/Buttons/Button';
import { EmployeeNominationHistory } from './EmployeeNominationHistory';
import { showSuccessToastMessage, showErrorToastMessage } from '../../../../component/Toasts/Toasts';
import "./HRBPDetails.scss"

interface ReviewModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    nomination: any;
}

const ReviewNominationModal = ({ show, onHide, onSuccess, nomination }: ReviewModalProps) => {
    const dispatch = useDispatch();
    const [remarks, setRemarks] = useState('');
    const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'approving' | 'rejecting'>('idle');
    //const [isSubmitting, setIsSubmitting] = useState(false);

    const [history, setHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(true);

    useEffect(() => {
        if (show && nomination?.employeeRef?._id) {
            setIsHistoryLoading(true);
            dispatch(actions.getEmployeeNominationHistoryAction(nomination.employeeRef._id, (res: any) => {
                if (res.success) {
                    setHistory(res.data);
                } else {
                    console.error("Failed to fetch nomination history:", res.error);
                    setHistory([]); 
                }
                setIsHistoryLoading(false);
            }));
        }
    }, [show, nomination, dispatch]); 

    const handleReview = (status: 'Approved' | 'Rejected') => {
        if (!remarks.trim()) {
            showErrorToastMessage('Remarks are required for approval or rejection.');
            return;
        }
        
        if (status === 'Approved') {
            setSubmissionStatus('approving');
        } else {
            setSubmissionStatus('rejecting');
        }
        
        const reviewData = {
            nominationId: nomination._id,
            status,
            remarks,
        };

        dispatch(actions.reviewNominationAction(reviewData, (res: any) => {
            setSubmissionStatus('idle'); 
            if (res.success) {
                showSuccessToastMessage(`Nomination has been ${status.toLowerCase()}.`);
                onSuccess();
                onHide();
            } else {
                showErrorToastMessage(res.error?.error_description || `Failed to ${status.toLowerCase()} nomination.`);
            }
        }));
    };

    const handleOnHide = () => {
        setRemarks('');
        setHistory([]);
        onHide();
    };

    const modalBodyContent = (
        <div className="container-fluid">
            {/* Display key info for context */}
            <p style={{ fontSize: "20px", fontWeight: "bold", textAlign: "center" }}>Nomination Details</p>
            <div className="mb-3 p-3 bg-light border rounded">
                <p><strong>Employee ID:</strong> {nomination.employeeRef?.employeeId}</p>
                <p><strong>Employee Name:</strong> {`${nomination.employeeRef?.candidateName} ${nomination.employeeRef?.middlename ?? ""} ${nomination.employeeRef?.lastname}`}</p>
                <p><strong>Employee Department:</strong> {`${nomination.employeeRef?.department}`}</p>
                <p><strong>Award:</strong> {nomination.awardRef?.awardCategory}</p>
                <p><strong>Reporting Manager:</strong> {`${nomination?.nominatedByRef?.candidateName} ${nomination?.nominatedByRef?.middlename ?? ""} ${nomination?.nominatedByRef?.lastname}`}</p>
                <p className="mb-0"><strong>Manager's Justification:</strong> {nomination.justification}</p>
            </div>

            {/* Display Nomination History --- */}
            <hr />
            <div className="mb-3">
                <h5 className="text-center mb-3">Past Nomination History</h5>
                <EmployeeNominationHistory 
                    nominations={history} 
                    isLoading={isHistoryLoading} 
                />
            </div>
            <hr />

            <Form.Group className="mb-3">
                <Form.Label><strong>HRBP Remarks</strong><span className="required">*</span></Form.Label>
                <Form.Control
                    as="textarea"
                    rows={4}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    required
                    placeholder="Provide your assessment and remarks..."
                />
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
                <Button
                    title={submissionStatus === 'rejecting' ? 'Rejecting...' : 'Reject'}
                    onClick={() => handleReview('Rejected')}
                    disabled={submissionStatus !== 'idle' || !remarks.trim()}
                    className="cancel-btn mr-2"
                />
                <Button
                    title={submissionStatus === 'approving' ? 'Approving...' : 'Approve'}
                    onClick={() => handleReview('Approved')}
                    disabled={submissionStatus !== 'idle' || !remarks.trim()}
                    className="add-submit-button"
                />
            </div>
        </div>
    );

    return (
        <ModalComponents
            show={show}
            onHide={handleOnHide}
            title="Review Nomination Request"
            modalBody={modalBodyContent}
            className="modal-lg"
        />
    );
};

export default ReviewNominationModal;